/** Shared bits for the chartmap-company mosaic sub-components. */

export const SORT_EVENTS_BY_TOGGLE = [
  {
    value: 'importance',
    label: 'Importance',
    icon: 'scale-balance',
    class: 'sorting-importance',
  },
  { value: 'count', label: 'Count', icon: 'pound', class: 'sorting-count' },
];

export function scrollIntoViewIfNotVisible(target: HTMLElement | null): void {
  if (!target) return;
  if (target.getBoundingClientRect().bottom > window.innerHeight) {
    target.scrollIntoView();
  }
  if (target.getBoundingClientRect().top < 0) {
    target.scrollIntoView();
  }
}

/** `temp1..4` buckets of `userState.scoreFocusedEventsAnalytics` (`{[eventId]: count}`). */
export type EventsAnalyticsBuckets =
  | Record<string, Record<string, number>>
  | null
  | undefined;

export interface TemporalityWidths {
  past: number;
  present: number;
  future: number;
  na: number;
  /** false = the event has no temporality counts at all (render the empty state, not a 100% N/A bar). */
  hasData: boolean;
}

const TEMPORALITY_BUCKET_KEYS = ['temp1', 'temp2', 'temp3', 'temp4'] as const;

const NO_TEMPORALITY: TemporalityWidths = {
  past: 0,
  present: 0,
  future: 0,
  na: 0,
  hasData: false,
};

/**
 * Segment widths for the per-event temporality mini-bar. Largest-remainder
 * rounding so the four segments always total exactly 100 (plain rounding could
 * overflow and push the N/A remainder negative).
 */
export function computeTemporalityWidths(
  analytics: EventsAnalyticsBuckets,
  itemId: string,
): TemporalityWidths {
  const counts = TEMPORALITY_BUCKET_KEYS.map(
    (key) => analytics?.[key]?.[itemId] ?? 0,
  );
  const sum = counts.reduce((acc, count) => acc + count, 0);
  if (sum <= 0) {
    return NO_TEMPORALITY;
  }

  const raw = counts.map((count) => (count * 100) / sum);
  const widths = raw.map(Math.floor);
  let remainder = 100 - widths.reduce((acc, width) => acc + width, 0);
  raw
    .map((value, index) => ({ index, fraction: value - widths[index] }))
    .sort((a, b) => b.fraction - a.fraction)
    .forEach(({ index }) => {
      if (remainder > 0) {
        widths[index]++;
        remainder--;
      }
    });

  const [past, present, future, na] = widths;
  return { past, present, future, na, hasData: true };
}

/** Column guard: any temporality bucket populated at all (analytics loaded + non-empty). */
export function hasTemporalityAnalytics(
  analytics: EventsAnalyticsBuckets,
): boolean {
  return TEMPORALITY_BUCKET_KEYS.some(
    (key) => Object.keys(analytics?.[key] ?? {}).length > 0,
  );
}
